// The words of /analysis that its core says: the names and explanations of the causes, the kinds of error and
// the ways out of the opening (domain/diagnosisView.ts), the windows (analysisWindow.ts), the sections
// (analysisSections.ts), the facts of the overview (analysisView.ts) and the frame of the page (Analysis.tsx).
import { words } from "../services/i18n.ts";

const s = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const t = words({
    en: {
        // Causes (they repeat cause.* in messages.properties, which the emails use)
        causeOpening: "Lost it from the opening",
        causeOpeningHelp: "You left the first 12 moves at -1 or worse and never got back to level (never better than -1). "
            + "It also counts when the move that decided the game was made inside the opening. It does not mean "
            + "you did not know the theory: it means the position the opening left you with was already bad.",
        causeBlunder: "Blunder with time to think",
        causeBlunderHelp: "The game was still on (not worse than -2) and one move of yours lost it, with plenty of time on "
            + "the clock.",
        causeTimeTrouble: "Blunder in time trouble",
        causeTimeTroubleHelp: "The game was decided by a serious mistake made with less than 10% of your starting time left.",
        causeFlagged: "Lost on time, position not lost",
        causeFlaggedHelp: "Your time ran out while the position was not lost (the engine had you no worse than -1.5). The "
            + "board did not decide this game; the clock did.",
        causeConversion: "Did not convert a winning position",
        causeConversionHelp: "You were clearly ahead (+2 or more for at least two of your moves, or the move that decided the "
            + "game was made from +2 or more) and still lost. The move marked is the one that took the "
            + "advantage away.",
        causeSlide: "Slipped away move by move",
        causeSlideHelp: "No single move decided it: the position got worse a little at a time, or you were already worse "
            + "and the opponent kept pressing until it was won.",
        causeKing: "Mated without being down material",
        causeKingHelp: "You were mated without being behind on material: an attack on your king you did not see coming.",
        causeUnexplained: "Unexplained",
        causeUnexplainedHelp: "None of the rules fits: usually an early resignation, or a game with incomplete data.",
        causeLevelDraw: "Level draw",
        causeLevelDrawHelp: "A draw where you were never clearly ahead.",
        causeWon: "Won",
        causeWonHelp: "A game you won. Nothing cost it; it is analysed to compare your losses with.",

        // Results
        losses: "Losses",
        wins: "Wins",
        draws: "Draws",
        all: "All",
        lossesNoun: "losses",
        winsNoun: "wins",
        drawsNoun: "draws",
        gamesNoun: "games",

        // Kinds of error
        kindMate: "Allowed mate",
        kindMateHelp: "After your move the opponent had mate in three or fewer.",
        kindHung: "Left material hanging",
        kindHungHelp: "The opponent's answer takes material straight away, at least 2 points (pawn 1, knight or bishop "
            + "3, rook 5, queen 9), and you do not win it back. Trained with the check before every move: what "
            + "can they take?",
        kindMissed: "Missed the opponent's reply",
        kindMissedHelp: "Only one or two answers punished your move: there was a concrete move you did not see, almost "
            + "always tactical (a check, a fork, a pin, a discovered attack). Trained with tactics puzzles.",
        kindMany: "Gave the opponent many good moves",
        kindManyHelp: "Three or more answers left you much worse: there was no single trap, the move spoiled the "
            + "position. Trained with plans and annotated games.",
        kindTime: "No time left to finish it",
        kindTimeHelp: "Only for won positions not converted: you were winning and the clock did it. You lost on time "
            + "with the game still standing, or the move that gave the advantage back was made with less than "
            + "10% of your time. Trained by playing won positions faster: simplify.",
        wentWrong: "Went wrong",
        wentWrongHelp: "The games where the opening went wrong for you: after 12 moves each you stood at -1 or worse, or the "
            + "game was already over before move 12. The share is of the games of that row, so a high one says that "
            + "opening or line leaves you with a bad position more often than not.",
        onlyMove: "Only one move held",
        onlyMoveHelp: "In that position only one move of yours kept the game: the second best was already a mistake. A "
            + "note beside any kind of error. Trained by learning to notice when it is time to stop and calculate.",
        clockFlag: "lost on time",
        clockFlagHelp: "Your time ran out with the game still standing.",
        clockTrouble: "only you short of time",
        clockTroubleHelp: "You made the move with less than 10% of your starting time and the opponent had more: your time "
            + "was spent worse.",
        clockMutual: "both short of time",
        clockMutualHelp: "Both of you were under 10% of the starting time: the game was decided in a scramble.",

        // How a game ended
        endMate: "checkmate",
        endResign: "resignation",
        endTime: "time",
        endAbandon: "abandoned",
        endDrawAgreed: "agreed draw",
        endRepetition: "repetition",
        endStalemate: "stalemate",
        endInsufficient: "insufficient material",
        endFifty: "50 moves",
        endTimeVsInsufficient: "time against insufficient material",
        endOther: "other",

        mate: "mate",
        mated: "mated",

        // Ways out of the opening
        exitWorse: "Out of it worse",
        exitWorseHelp: "After 12 moves each you stood at -1 or worse: the opening left you a bad position to play, "
            + "whatever came after.",
        exitLevel: "Out of it level",
        exitLevelHelp: "After 12 moves each the position was between -1 and +1: the opening did its job, and what "
            + "came after was the rest of the game.",
        exitBetter: "Out of it better",
        exitBetterHelp: "After 12 moves each you stood at +1 or better: the opening gave you the advantage.",
        exitInside: "Over before move 12",
        exitInsideHelp: "The game ended inside the opening: a trap, a piece left early, or a quick resignation.",

        // Asking for an analysis
        last50: "The last 50",
        last100: "The last 100",
        allOfThem: "All of them",
        underAMinute: "under a minute",
        aboutMinutes: (minutes: number) => `about ${minutes} minute${s(minutes, "", "s")}`,
        aboutHours: (hours: number, rest: number) => `about ${hours} h${rest >= 5 ? ` ${rest} min` : ""}`,

        // The windows
        thisWeek: "This week",
        last4Weeks: "Last 4 weeks",
        weeksTo: (weeks: number, day: string) => `${weeks} weeks to ${day}`,
        thisWeekPhrase: "this week",
        last4WeeksPhrase: "the last four weeks",
        weekBefore: "the week before",
        weeksBefore: (weeks: number) => `the ${weeks} weeks before`,
        dayBefore: "the day before",
        daysBefore: (days: number) => `the ${days} days before`,

        // The sections
        secOpenings: "Openings",
        secOpeningsTitle: "How your openings behave",
        secOpeningsLead: "Which openings paid and which cost you, and how you stood on the way out of them.",
        secWhy: "Why you lose",
        secWhyTitle: "What decided your losses",
        secWhyLead: "The one thing that turned each analysed loss, and the kind of move it was.",
        secAdvantages: "Advantages",
        secAdvantagesTitle: "The advantages that slip away",
        secAdvantagesLead: "Every time you stood clearly better, won or lost: how many you kept, and where the others went.",
        secPhases: "By phase",
        secPhasesTitle: "Your game, phase by phase",
        secPhasesLead: "How well you play the opening, the middlegame and the endgame, beside your opponents in the same games.",
        secClock: "Clock usage",
        secClockTitle: "How you spend your clock",
        secClockLead: "Where your time goes, quarter by quarter, and whether it leaves you behind your opponent.",
        secOpponents: "Opponents",
        secOpponentsTitle: "Who you played",
        secOpponentsLead: "How strong they were against you, and how you did at each distance.",
        secHours: "When you play",
        secHoursTitle: "The hours you play at",
        secHoursLead: "Your games by the time of day, in your own clock.",

        // The overview's facts
        noneAnalysed: "None analysed",
        tooFewToRead: (analysed: number, games: number) => `${analysed} of ${games} analysed — too few to read`,
        wentWrongNote: (analysed: number, games: number, worse: number) => `${analysed} of ${games} analysed · ${worse} went wrong`,
        months: "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",
        monthsFull: "January,February,March,April,May,June,July,August,September,October,November,December",
        monthYear: (month: string, year: string) => `${month} ${year}`,
        white: "White",
        black: "Black",
        gamesBasis: (games: number) => `${games} games`,
        analysedLossesBasis: (losses: number) => `${losses} analysed losses`,
        analysedBasis: (games: number, losses: boolean) => `${games} analysed ${losses ? "losses" : "games"}`,
        openingTitle: (family: string) => `The ${family} is costing you points`,
        openingText: (colour: string, games: number, record: string, score: string, average: string, worth: string) =>
            `As ${colour}, ${games} games: ${record}. You take ${score} points out of 10 there — across all your games `
            + `you take ${average}. Bringing it up to your usual is worth ${worth}.`,
        findBranch: "Find the branch in your tree",
        conversionTitle: (n: number) => `You let ${n} won game${s(n, "", "s")} go`,
        conversionText: (n: number, losses: number, share: string) =>
            `In ${n} of your ${losses} analysed losses (${share}) you were +2 or better.`,
        keepAdvantages: "Keep those advantages · Your positions",
        exitTitle: (share: string) => `${share} go wrong in the opening`,
        exitText: (n: number) => `${n} times you came out of move 12 at −1 or worse, or the game was over by then.`,
        studyWorstLines: "Study the lines your games go worst in",
        clockTitle: (share: string) => `The clock decided ${share} of your losses`,
        clockText: (n: number) => `${n} lost on time or to a move made short of it.`,
        clock: "Clock",
        missedTitle: (share: string) => `You missed the reply in ${share} of them`,
        missedText: (n: number) => `${n} losses came from a move whose answer you did not see.`,
        blindfoldIt: "See the board without it · Blindfold",
        mistakes: "Mistakes",

        // The page
        finished: "Analysis finished",
        finishedText: "Your games are read and the numbers on this page are fresh.",
        failed: "Analysis failed",
        failedText: "Nothing was analysed and nothing was counted. Ask for it again.",
        notRead: "Your games could not be read. Try again in a moment.",
        everything: "Everything on one page, to read or print →",
        yourData: "Your data: what is analysed and what is not →",
        sections: "Sections",
        readsImported: "Reads every imported game",
        readsAnalysed: "Reads analysed games only",
        partRecord: "Which openings paid",
        partExit: "How you come out of them",
        partFine: "When it was not the opening",
        partCauses: "What decided them",
        partBlunder: "When you blunder",
        partKinds: "The kind of mistake",
        partForgiven: "How much you are forgiven",
        partRatings: "Against whom",
        partChances: "The advantages you had",
        partWhere: "Where they went",
        partEach: "Each one",
        partQuarters: "Quarter by quarter",
        partGap: "Ahead or behind",
        partTime: "Lost on time",
        partTrouble: "Whose time trouble",
        partAhead: "Short of time while winning",
        partRating: "How strong they were",
        partGames: "The games themselves",
        studyList: "Study list",
        readingGames: "Reading your games…",
        backToWeek: "← Back to this week",
        importedGames: "Imported games",
        recordAllTime: (wins: string, draws: string, losses: string) => `${wins} wins · ${draws} draws · ${losses} losses, all time.`,
        freeParts: "Results, openings, score by opening, the clock, who you played, when you played:",
        freeAlways: "free, always, for every game.",
        manageImports: "Manage your imports",
        analysedByStockfish: "Analysed by Stockfish",
        ofGames: (games: string, share: string) => `of ${games} · ${share}`,
        analysedRecord: (losses: string, wins: string, draws: string, since: string | null) =>
            `${losses} losses · ${wins} wins · ${draws} draws${since ? `, since ${since}` : ""}.`,
        countedParts: "Why you lose, why you win, the kind of mistake, the way out of the opening:",
        countedOnce: "counted per game, once.",
        allowance: "Your allowance",
        free: "Free",
        left: (n: string) => `${n} left`,
        bought: "Bought",
        subscription: "Subscription",
        on: "On",
        off: "Off",
        beingAnalysed: "Being analysed",
        reading: "Reading…",
        allowanceText: "Your last 300 games are analysed free. After that, games are analysed one by one, or a few minutes after they end with a subscription.",
        freeNote: "Looking and filtering is free and unlimited. A game is never counted twice, and reading them again when the rules change is on us.",
        coverageTitle: "Coverage, month by month",
        coverageLead: "The games of each month and how many of them are analysed. The free analyses are your newest games, so the sections that read analysed games are reading your last weeks; anything older is in the free parts only.",
        monthsLabel: "Games per month and the analysed part of them",
        monthTitle: (month: string, games: number, analysed: number) => `${month}: ${games} games, ${analysed} analysed`,
        analysed: "Analysed",
        importedOnly: "Imported only",
        olderNotShown: (n: string) => `${n} older games are not shown`,
        backToSections: "← This week, section by section",
        downloadPdf: "Download PDF",
        costingMost: "What is costing you most",
        nothingStands: "Nothing stands out yet across your games.",
        nothingAnalysed: "Nothing analysed yet",
        noneOfYour: (games: string, noun: string) =>
            `None of your ${games} ${noun} has been analysed, and the rest of this page is made of what Stockfish finds when it reads a game move by move.`,
        analyseYourGames: "Analyse your games",
        printStamp: "Sparring Chess · sparringchess.com/analysis/report · every game ·",
    },
    es: {
        causeOpening: "Perdida desde la apertura",
        causeOpeningHelp: "Saliste de las 12 primeras jugadas con -1 o peor y nunca volviste a la igualdad (nunca mejor que -1). "
            + "También cuenta cuando la jugada que decidió la partida se hizo dentro de la apertura. No quiere decir "
            + "que no supieras la teoría: quiere decir que la posición que te dejó la apertura ya era mala.",
        causeBlunder: "Error grave con tiempo para pensar",
        causeBlunderHelp: "La partida seguía viva (no peor que -2) y una jugada tuya la perdió, con tiempo de sobra en el reloj.",
        causeTimeTrouble: "Error grave con poco tiempo en el reloj",
        causeTimeTroubleHelp: "La partida la decidió un error grave hecho con menos del 10% de tu tiempo inicial.",
        causeFlagged: "Perdida por tiempo, con la posición sin perder",
        causeFlaggedHelp: "Se te acabó el tiempo con la posición sin perder (el motor no te daba peor que -1,5). "
            + "Esta partida no la decidió el tablero, sino el reloj.",
        causeConversion: "No convirtió una posición ganadora",
        causeConversionHelp: "Ibas claramente por delante (+2 o más durante al menos dos de tus jugadas, o la jugada que "
            + "decidió la partida se hizo desde +2 o más) y aun así perdiste. La jugada marcada es la que se llevó la ventaja.",
        causeSlide: "Se escapó jugada a jugada",
        causeSlideHelp: "Ninguna jugada sola la decidió: la posición empeoró poco a poco, o ya ibas peor y el rival "
            + "siguió apretando hasta ganarla.",
        causeKing: "Mate sin ir por detrás en material",
        causeKingHelp: "Te dieron mate sin ir por detrás en material: un ataque a tu rey que no viste venir.",
        causeUnexplained: "Sin explicar",
        causeUnexplainedHelp: "No encaja ninguna regla: normalmente un abandono temprano, o una partida con datos incompletos.",
        causeLevelDraw: "Tablas igualadas",
        causeLevelDrawHelp: "Unas tablas en las que nunca fuiste claramente por delante.",
        causeWon: "Ganada",
        causeWonHelp: "Una partida que ganaste. No hubo nada que la costara; se analiza para comparar con ella tus derrotas.",

        losses: "Derrotas",
        wins: "Victorias",
        draws: "Tablas",
        all: "Todas",
        lossesNoun: "derrotas",
        winsNoun: "victorias",
        drawsNoun: "tablas",
        gamesNoun: "partidas",

        kindMate: "Permitió mate",
        kindMateHelp: "Tras tu jugada, el rival tenía mate en tres o menos.",
        kindHung: "Dejó material colgado",
        kindHungHelp: "La respuesta del rival gana material de inmediato, al menos 2 puntos (peón 1, caballo o alfil 3, "
            + "torre 5, dama 9), y no lo recuperas. Se entrena con la comprobación antes de cada jugada: ¿qué me pueden comer?",
        kindMissed: "No vio la respuesta del rival",
        kindMissedHelp: "Solo una o dos respuestas castigaban tu jugada: había una jugada concreta que no viste, casi "
            + "siempre táctica (un jaque, una horquilla, una clavada, un ataque a la descubierta). Se entrena con problemas tácticos.",
        kindMany: "Le dio al rival muchas buenas jugadas",
        kindManyHelp: "Tres o más respuestas te dejaban mucho peor: no había una trampa concreta, la jugada estropeó la "
            + "posición. Se entrena con planes y partidas comentadas.",
        kindTime: "Sin tiempo para rematarla",
        kindTimeHelp: "Solo para posiciones ganadas que no se convirtieron: ibas ganando y lo decidió el reloj. Perdiste "
            + "por tiempo con la partida en pie, o la jugada que devolvió la ventaja se hizo con menos del 10% de tu "
            + "tiempo. Se entrena jugando más rápido las posiciones ganadas: simplifica.",
        wentWrong: "Se torció",
        wentWrongHelp: "Las partidas en las que la apertura te salió mal: tras 12 jugadas cada uno estabas en -1 o peor, o "
            + "la partida ya había terminado antes de la jugada 12. El porcentaje es sobre las partidas de esa fila, así "
            + "que uno alto dice que esa apertura o línea te deja una mala posición más veces que no.",
        onlyMove: "Solo una jugada aguantaba",
        onlyMoveHelp: "En esa posición solo una jugada tuya mantenía la partida: la segunda mejor ya era un error. Una "
            + "nota junto a cualquier clase de error. Se entrena aprendiendo a darse cuenta de cuándo toca pararse a calcular.",
        clockFlag: "perdida por tiempo",
        clockFlagHelp: "Se te acabó el tiempo con la partida en pie.",
        clockTrouble: "solo tú con poco tiempo",
        clockTroubleHelp: "Hiciste la jugada con menos del 10% de tu tiempo inicial y el rival tenía más: tu tiempo se "
            + "gastó peor.",
        clockMutual: "los dos con poco tiempo",
        clockMutualHelp: "Los dos estabais por debajo del 10% del tiempo inicial: la partida se decidió a la carrera.",

        endMate: "jaque mate",
        endResign: "abandono",
        endTime: "tiempo",
        endAbandon: "abandonada",
        endDrawAgreed: "tablas de mutuo acuerdo",
        endRepetition: "repetición",
        endStalemate: "ahogado",
        endInsufficient: "material insuficiente",
        endFifty: "50 jugadas",
        endTimeVsInsufficient: "tiempo contra material insuficiente",
        endOther: "otro",

        mate: "mate",
        mated: "mate en contra",

        exitWorse: "Salió peor",
        exitWorseHelp: "Tras 12 jugadas cada uno estabas en -1 o peor: la apertura te dejó una mala posición que jugar, "
            + "viniera lo que viniera después.",
        exitLevel: "Salió igualada",
        exitLevelHelp: "Tras 12 jugadas cada uno la posición estaba entre -1 y +1: la apertura cumplió, y lo que vino "
            + "después fue el resto de la partida.",
        exitBetter: "Salió mejor",
        exitBetterHelp: "Tras 12 jugadas cada uno estabas en +1 o mejor: la apertura te dio la ventaja.",
        exitInside: "Acabó antes de la jugada 12",
        exitInsideHelp: "La partida terminó dentro de la apertura: una trampa, una pieza perdida pronto o un abandono rápido.",

        last50: "Las últimas 50",
        last100: "Las últimas 100",
        allOfThem: "Todas",
        underAMinute: "menos de un minuto",
        aboutMinutes: (minutes: number) => `unos ${minutes} minuto${s(minutes, "", "s")}`,
        aboutHours: (hours: number, rest: number) => `unas ${hours} h${rest >= 5 ? ` ${rest} min` : ""}`,

        thisWeek: "Esta semana",
        last4Weeks: "Últimas 4 semanas",
        weeksTo: (weeks: number, day: string) => `${weeks} semanas hasta el ${day}`,
        thisWeekPhrase: "esta semana",
        last4WeeksPhrase: "las últimas cuatro semanas",
        weekBefore: "la semana anterior",
        weeksBefore: (weeks: number) => `las ${weeks} semanas anteriores`,
        dayBefore: "el día anterior",
        daysBefore: (days: number) => `los ${days} días anteriores`,

        secOpenings: "Aperturas",
        secOpeningsTitle: "Cómo se comportan tus aperturas",
        secOpeningsLead: "Qué aperturas te dieron puntos y cuáles te los costaron, y cómo saliste de ellas.",
        secWhy: "Por qué pierdes",
        secWhyTitle: "Qué decidió tus derrotas",
        secWhyLead: "Lo que dio la vuelta a cada derrota analizada, y qué clase de jugada fue.",
        secAdvantages: "Ventajas",
        secAdvantagesTitle: "Las ventajas que se escapan",
        secAdvantagesLead: "Cada vez que estuviste claramente mejor, ganaras o perdieras: cuántas conservaste y adónde fueron las demás.",
        secPhases: "Por fases",
        secPhasesTitle: "Tu juego, fase a fase",
        secPhasesLead: "Lo bien que juegas la apertura, el medio juego y el final, junto a tus rivales en las mismas partidas.",
        secClock: "Uso del reloj",
        secClockTitle: "Cómo gastas tu reloj",
        secClockLead: "Adónde se va tu tiempo, cuarto a cuarto, y si te deja por detrás de tu rival.",
        secOpponents: "Rivales",
        secOpponentsTitle: "Contra quién jugaste",
        secOpponentsLead: "Lo fuertes que eran frente a ti, y cómo te fue a cada distancia.",
        secHours: "Cuándo juegas",
        secHoursTitle: "Las horas a las que juegas",
        secHoursLead: "Tus partidas según la hora del día, en tu propio reloj.",

        noneAnalysed: "Ninguna analizada",
        tooFewToRead: (analysed: number, games: number) => `${analysed} de ${games} analizadas: demasiado pocas para leerlas`,
        wentWrongNote: (analysed: number, games: number, worse: number) => `${analysed} de ${games} analizadas · ${worse} se torcieron`,
        months: "ene,feb,mar,abr,may,jun,jul,ago,sep,oct,nov,dic",
        monthsFull: "enero,febrero,marzo,abril,mayo,junio,julio,agosto,septiembre,octubre,noviembre,diciembre",
        monthYear: (month: string, year: string) => `${month} de ${year}`,
        white: "blancas",
        black: "negras",
        gamesBasis: (games: number) => `${games} partidas`,
        analysedLossesBasis: (losses: number) => `${losses} derrotas analizadas`,
        analysedBasis: (games: number, losses: boolean) => `${games} ${losses ? "derrotas analizadas" : "partidas analizadas"}`,
        openingTitle: (family: string) => `${family} te está costando puntos`,
        openingText: (colour: string, games: number, record: string, score: string, average: string, worth: string) =>
            `Con ${colour}, ${games} partidas: ${record}. Ahí sacas ${score} puntos de 10; en todas tus partidas `
            + `sacas ${average}. Subirla a tu nivel habitual vale ${worth}.`,
        findBranch: "Busca la variante en tu árbol",
        conversionTitle: (n: number) => `Dejaste escapar ${n} partida${s(n, "", "s")} ganada${s(n, "", "s")}`,
        conversionText: (n: number, losses: number, share: string) =>
            `En ${n} de tus ${losses} derrotas analizadas (${share}) estuviste en +2 o mejor.`,
        keepAdvantages: "Conserva esas ventajas · Tus posiciones",
        exitTitle: (share: string) => `El ${share} se tuercen en la apertura`,
        exitText: (n: number) => `${n} veces saliste de la jugada 12 con −1 o peor, o la partida ya había terminado.`,
        studyWorstLines: "Estudia las líneas en las que peor te va",
        clockTitle: (share: string) => `El reloj decidió el ${share} de tus derrotas`,
        clockText: (n: number) => `${n} perdidas por tiempo o por una jugada hecha casi sin él.`,
        clock: "Reloj",
        missedTitle: (share: string) => `No viste la respuesta en el ${share} de ellas`,
        missedText: (n: number) => `${n} derrotas vinieron de una jugada cuya respuesta no viste.`,
        blindfoldIt: "Ve el tablero sin verlo · A ciegas",
        mistakes: "Errores",

        finished: "Análisis terminado",
        finishedText: "Tus partidas están leídas y los números de esta página están al día.",
        failed: "El análisis ha fallado",
        failedText: "No se ha analizado ni contado nada. Vuelve a pedirlo.",
        notRead: "No hemos podido leer tus partidas. Inténtalo de nuevo en un momento.",
        everything: "Todo en una página, para leer o imprimir →",
        yourData: "Tus datos: qué está analizado y qué no →",
        sections: "Secciones",
        readsImported: "Lee todas las partidas importadas",
        readsAnalysed: "Lee solo las partidas analizadas",
        partRecord: "Qué aperturas dieron puntos",
        partExit: "Cómo sales de ellas",
        partFine: "Cuando no fue la apertura",
        partCauses: "Qué las decidió",
        partBlunder: "Cuándo cometes errores graves",
        partKinds: "La clase de error",
        partForgiven: "Cuánto te perdonan",
        partRatings: "Contra quién",
        partChances: "Las ventajas que tuviste",
        partWhere: "Adónde fueron",
        partEach: "Una a una",
        partQuarters: "Cuarto a cuarto",
        partGap: "Por delante o por detrás",
        partTime: "Perdidas por tiempo",
        partTrouble: "De quién son los apuros de tiempo",
        partAhead: "Sin tiempo yendo ganando",
        partRating: "Lo fuertes que eran",
        partGames: "Las partidas en sí",
        studyList: "Lista de estudio",
        readingGames: "Leyendo tus partidas…",
        backToWeek: "← Volver a esta semana",
        importedGames: "Partidas importadas",
        recordAllTime: (wins: string, draws: string, losses: string) => `${wins} victorias · ${draws} tablas · ${losses} derrotas, en total.`,
        freeParts: "Resultados, aperturas, puntuación por apertura, el reloj, contra quién jugaste, cuándo jugaste:",
        freeAlways: "gratis, siempre, para cada partida.",
        manageImports: "Gestionar tus importaciones",
        analysedByStockfish: "Analizadas por Stockfish",
        ofGames: (games: string, share: string) => `de ${games} · ${share}`,
        analysedRecord: (losses: string, wins: string, draws: string, since: string | null) =>
            `${losses} derrotas · ${wins} victorias · ${draws} tablas${since ? `, desde ${since}` : ""}.`,
        countedParts: "Por qué pierdes, por qué ganas, la clase de error, la salida de la apertura:",
        countedOnce: "se cuenta por partida, una sola vez.",
        allowance: "Tu saldo",
        free: "Gratis",
        left: (n: string) => `quedan ${n}`,
        bought: "Compradas",
        subscription: "Suscripción",
        on: "Activa",
        off: "No",
        beingAnalysed: "Analizándose",
        reading: "Leyendo…",
        allowanceText: "Tus últimas 300 partidas se analizan gratis. A partir de ahí, las partidas se analizan una a una, o unos minutos después de terminar con una suscripción.",
        freeNote: "Mirar y filtrar es gratis y sin límite. Una partida nunca se cuenta dos veces, y volver a leerlas cuando cambian las reglas corre de nuestra cuenta.",
        coverageTitle: "Cobertura, mes a mes",
        coverageLead: "Las partidas de cada mes y cuántas de ellas están analizadas. Los análisis gratis son tus partidas más recientes, así que las secciones que leen partidas analizadas leen tus últimas semanas; lo más antiguo solo está en las partes gratis.",
        monthsLabel: "Partidas por mes y la parte analizada de ellas",
        monthTitle: (month: string, games: number, analysed: number) => `${month}: ${games} partidas, ${analysed} analizadas`,
        analysed: "Analizadas",
        importedOnly: "Solo importadas",
        olderNotShown: (n: string) => `No se muestran ${n} partidas más antiguas`,
        backToSections: "← Esta semana, sección a sección",
        downloadPdf: "Descargar PDF",
        costingMost: "Lo que más te está costando",
        nothingStands: "Todavía no destaca nada en tus partidas.",
        nothingAnalysed: "Todavía no hay nada analizado",
        noneOfYour: (games: string, noun: string) =>
            `Ninguna de tus ${games} ${noun} se ha analizado, y el resto de esta página está hecho de lo que encuentra Stockfish cuando lee una partida jugada a jugada.`,
        analyseYourGames: "Analizar tus partidas",
        printStamp: "Sparring Chess · sparringchess.com/analysis/report · todas las partidas ·",
    },
});
